"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  MinusIcon,
  PackagePlusIcon,
  PlusIcon,
  ShoppingBagIcon,
  TicketPercentIcon,
  Trash2Icon,
} from "lucide-react"
import Link from "next/link"
import type { ReactNode } from "react"
import { toast } from "sonner"
import {
  type CartSnapshot,
  getCartItems,
  removeCartItem,
  updateCartItemQuantity,
} from "@/api/cart"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"

const brl = new Intl.NumberFormat("pt-BR", {
  currency: "BRL",
  style: "currency",
})

const toCurrency = (value: number) => brl.format(value)

const recalculateSnapshot = (snapshot: CartSnapshot): CartSnapshot => {
  const subtotal = snapshot.items.reduce(
    (accumulator, item) => accumulator + item.lineTotal,
    0
  )
  const cartCount = snapshot.items.reduce(
    (accumulator, item) => accumulator + item.quantity,
    0
  )

  return {
    ...snapshot,
    cartCount,
    totals: {
      ...snapshot.totals,
      subtotal,
      total: subtotal,
    },
  }
}

const applyQuantityOptimisticChange = (
  snapshot: CartSnapshot,
  key: string,
  quantity: number
): CartSnapshot => {
  return recalculateSnapshot({
    ...snapshot,
    items: snapshot.items.map((item) => {
      if (item.key !== key) {
        return item
      }

      return {
        ...item,
        quantity,
        lineTotal: item.price * quantity,
      }
    }),
  })
}

const applyRemoveOptimisticChange = (
  snapshot: CartSnapshot,
  key: string
): CartSnapshot => {
  return recalculateSnapshot({
    ...snapshot,
    items: snapshot.items.filter((item) => item.key !== key),
  })
}

export function CartSheet() {
  const queryClient = useQueryClient()

  const { data, isError } = useQuery({
    queryKey: ["cart", "items"],
    queryFn: getCartItems,
    staleTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  })

  const updateQuantityMutation = useMutation({
    mutationFn: updateCartItemQuantity,
    onMutate: async ({ key, quantity }) => {
      await queryClient.cancelQueries({ queryKey: ["cart", "items"] })

      const previousSnapshot = queryClient.getQueryData<CartSnapshot>([
        "cart",
        "items",
      ])

      if (previousSnapshot) {
        queryClient.setQueryData(
          ["cart", "items"],
          applyQuantityOptimisticChange(previousSnapshot, key, quantity)
        )
      }

      const toastId = toast.loading("Atualizando quantidade...")

      return { previousSnapshot, toastId }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousSnapshot) {
        queryClient.setQueryData(["cart", "items"], context.previousSnapshot)
      }

      toast.error("Nao foi possivel atualizar a quantidade.", {
        id: context?.toastId,
      })
    },
    onSuccess: (_data, _variables, context) => {
      toast.success("Quantidade atualizada.", {
        id: context?.toastId,
      })
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["cart", "items"] })
    },
  })

  const removeItemMutation = useMutation({
    mutationFn: removeCartItem,
    onMutate: async ({ key }) => {
      await queryClient.cancelQueries({ queryKey: ["cart", "items"] })

      const previousSnapshot = queryClient.getQueryData<CartSnapshot>([
        "cart",
        "items",
      ])

      if (previousSnapshot) {
        queryClient.setQueryData(
          ["cart", "items"],
          applyRemoveOptimisticChange(previousSnapshot, key)
        )
      }

      const toastId = toast.loading("Removendo item do carrinho...")

      return { previousSnapshot, toastId }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousSnapshot) {
        queryClient.setQueryData(["cart", "items"], context.previousSnapshot)
      }

      toast.error("Nao foi possivel remover o item.", {
        id: context?.toastId,
      })
    },
    onSuccess: (_data, _variables, context) => {
      toast.success("Item removido do carrinho.", { id: context?.toastId })
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["cart", "items"] })
    },
  })

  const items = data?.items ?? []
  const itemsCount = data?.cartCount ?? 0
  const subtotal = data?.totals.subtotal ?? 0
  const total = data?.totals.total ?? 0

  const isItemBusy = (itemKey: string): boolean => {
    const isUpdatingCurrentItem =
      updateQuantityMutation.isPending &&
      updateQuantityMutation.variables?.key === itemKey
    const isRemovingCurrentItem =
      removeItemMutation.isPending &&
      removeItemMutation.variables?.key === itemKey

    return isUpdatingCurrentItem || isRemovingCurrentItem
  }

  const handleIncreaseQuantity = (key: string, quantity: number) => {
    updateQuantityMutation.mutate({
      key,
      quantity: quantity + 1,
    })
  }

  const handleDecreaseQuantity = (key: string, quantity: number) => {
    if (quantity <= 1) {
      return
    }

    updateQuantityMutation.mutate({
      key,
      quantity: quantity - 1,
    })
  }

  const handleRemoveItem = (key: string) => {
    removeItemMutation.mutate({ key })
  }

  let cartContent: ReactNode

  if (isError) {
    cartContent = (
      <div className='rounded-xl border border-destructive/30 p-6 text-center'>
        <p className='font-medium'>Nao foi possivel carregar o carrinho</p>
        <p className='mt-1 text-muted-foreground text-sm'>
          Tente novamente em instantes.
        </p>
      </div>
    )
  } else if (items.length === 0) {
    cartContent = (
      <div className='rounded-xl border border-dashed p-6 text-center'>
        <p className='font-medium'>Seu carrinho esta vazio</p>
        <p className='mt-1 text-muted-foreground text-sm'>
          Adicione produtos para ver o resumo aqui.
        </p>
      </div>
    )
  } else {
    cartContent = (
      <ul className='space-y-4'>
        {items.map((item) => {
          const busy = isItemBusy(item.key)

          return (
            <li className='rounded-xl border p-4' key={item.key}>
              <div className='flex items-start justify-between gap-4'>
                <p className='font-medium text-sm leading-6'>{item.name}</p>
                <button
                  aria-label='Remover item'
                  className='text-muted-foreground hover:text-destructive'
                  disabled={busy}
                  onClick={() => {
                    handleRemoveItem(item.key)
                  }}
                  type='button'
                >
                  <Trash2Icon size={16} />
                </button>
              </div>

              <div className='mt-3 flex items-center justify-between gap-3'>
                <div className='flex items-center rounded-md border'>
                  <button
                    aria-label='Diminuir quantidade'
                    className='px-2 py-1 hover:bg-muted'
                    disabled={busy || item.quantity <= 1}
                    onClick={() => {
                      handleDecreaseQuantity(item.key, item.quantity)
                    }}
                    type='button'
                  >
                    <MinusIcon size={14} />
                  </button>
                  <span className='min-w-8 border-x px-2 py-1 text-center text-sm'>
                    {item.quantity}
                  </span>
                  <button
                    aria-label='Aumentar quantidade'
                    className='px-2 py-1 hover:bg-muted'
                    disabled={busy}
                    onClick={() => {
                      handleIncreaseQuantity(item.key, item.quantity)
                    }}
                    type='button'
                  >
                    <PlusIcon size={14} />
                  </button>
                </div>

                <p className='font-semibold text-sm'>
                  {toCurrency(item.lineTotal)}
                </p>
              </div>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button className='relative' size='icon' variant='outline'>
          <ShoppingBagIcon aria-hidden='true' size={18} />
          <span className='sr-only'>Abrir carrinho</span>
          <span className='absolute -top-1 -right-1 flex size-5 items-center justify-center rounded-full bg-primary font-semibold text-[10px] text-primary-foreground'>
            {itemsCount}
          </span>
        </Button>
      </SheetTrigger>

      <SheetContent className='flex h-full flex-col'>
        <SheetHeader className='border-b px-6 py-4'>
          <div className='flex items-center gap-2'>
            <PackagePlusIcon aria-hidden='true' size={20} />
            <SheetTitle>Seu carrinho</SheetTitle>
          </div>
          <SheetDescription>
            {itemsCount} {itemsCount === 1 ? "item" : "itens"}
          </SheetDescription>
        </SheetHeader>

        <div className='min-h-0 flex-1 overflow-y-auto px-6 py-4'>
          {cartContent}
        </div>

        <SheetFooter className='border-t bg-background px-6 py-4'>
          <button
            className='flex items-center gap-2 text-muted-foreground text-sm hover:text-foreground'
            type='button'
          >
            <TicketPercentIcon aria-hidden='true' size={16} />
            Utilizar cupom
          </button>

          <div className='space-y-2 border-border border-y py-3'>
            <div className='flex items-center justify-between'>
              <span className='font-semibold'>Subtotal</span>
              <span>{toCurrency(subtotal)}</span>
            </div>
            <div className='flex items-start justify-between gap-4'>
              <span className='font-semibold'>Entrega</span>
              <span className='max-w-44 text-right text-muted-foreground'>
                Nenhuma opcao de entrega foi encontrada para Sao Paulo.
              </span>
            </div>
            <div className='flex items-center justify-between border-t border-dashed pt-2'>
              <span className='font-semibold text-lg'>Total</span>
              <span className='font-semibold text-lg'>{toCurrency(total)}</span>
            </div>
          </div>

          <Button asChild className='w-full'>
            <Link href='/shop'>Continuar comprando</Link>
          </Button>
          <Button asChild className='w-full'>
            <Link href='/carrinho'>Ver carrinho</Link>
          </Button>
          <Button asChild className='w-full'>
            <Link href='/finalizar-comprar'>Finalizar compra</Link>
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
